<script setup lang="ts">
/**
 * The footer's social links: order, platform, URL and visibility.
 *
 * Rows are edited in place and saved on change. Order is set by dragging a
 * row by its handle (or the arrow buttons, which also cover touch — HTML5
 * drag-and-drop does not fire there); a reorder writes `sortOrder = index`
 * to every row whose position changed. The public footer reads the same
 * list (`/api/cms/social-links`: active only, by `sortOrder`).
 */
import { computed, ref } from 'vue';

import { SOCIAL_PLATFORMS, iconFor } from '../landing/social-icons';
import { adminApi, type SocialRow } from './admin-api';
import { usePane } from './pane';

const social = usePane(() => adminApi.social());
const rows = computed(() => social.data.value ?? []);

/** A stored platform outside the picker's list stays selectable on its own row. */
function optionsFor(row: SocialRow): { value: string; label: string }[] {
  return SOCIAL_PLATFORMS.some((p) => p.value === row.platform)
    ? SOCIAL_PLATFORMS
    : [{ value: row.platform, label: row.platform }, ...SOCIAL_PLATFORMS];
}

const isUrl = (v: string): boolean => /^https?:\/\/\S+$/i.test(v.trim());
const rowError = ref<string | null>(null);

function patch(row: SocialRow, body: Partial<SocialRow>): void {
  void social.act(() => adminApi.socialPatch(row.id, body));
}

function saveUrl(row: SocialRow, event: Event): void {
  const input = event.target as HTMLInputElement;
  const value = input.value.trim();
  if (value === row.url) return;
  if (!isUrl(value)) {
    rowError.value = 'Links must start with http:// or https://';
    input.value = row.url;
    return;
  }
  rowError.value = null;
  patch(row, { url: value });
}

// ─── Reordering ──────────────────────────────────────────────────────────────

const dragFrom = ref<number | null>(null);
const dragOver = ref<number | null>(null);

function move(from: number, to: number): void {
  if (from === to || to < 0 || to >= rows.value.length) return;
  const next = [...rows.value];
  next.splice(to, 0, next.splice(from, 1)[0]);
  const changed = next.map((r, i) => ({ r, i })).filter(({ r, i }) => r.sortOrder !== i);
  void social.act(() => Promise.all(changed.map(({ r, i }) => adminApi.socialPatch(r.id, { sortOrder: i }))));
}

function onDrop(to: number): void {
  if (dragFrom.value !== null) move(dragFrom.value, to);
  dragFrom.value = dragOver.value = null;
}

// ─── Add ─────────────────────────────────────────────────────────────────────

const newPlatform = ref(SOCIAL_PLATFORMS[0].value);
const newUrl = ref('');
function addSocial(): void {
  if (!isUrl(newUrl.value)) {
    rowError.value = 'Links must start with http:// or https://';
    return;
  }
  rowError.value = null;
  const sortOrder = rows.value.reduce((max, r) => Math.max(max, r.sortOrder + 1), 0);
  void social
    .act(() => adminApi.socialCreate({ platform: newPlatform.value, url: newUrl.value.trim(), sortOrder }))
    .then(() => {
      newUrl.value = '';
    });
}
</script>

<template>
  <section class="social-editor">
    <h2>Social links</h2>
    <p class="hint">Shown in the site footer, in this order. Drag a row by its handle to reorder.</p>
    <p v-if="social.error.value" class="bad">{{ social.error.value }}</p>
    <p v-if="rowError" class="bad">{{ rowError }}</p>

    <ul class="social-rows" data-test="social-rows">
      <li
        v-for="(row, i) in rows"
        :key="row.id"
        class="social-row"
        :class="{ over: dragOver === i && dragFrom !== i, off: !row.active }"
        draggable="true"
        @dragstart="dragFrom = i"
        @dragover.prevent="dragOver = i"
        @drop.prevent="onDrop(i)"
        @dragend="dragFrom = dragOver = null"
      >
        <span class="handle" title="Drag to reorder" aria-hidden="true">⠿</span>
        <span class="icon-chip">
          <img v-if="iconFor(row.platform)" :src="iconFor(row.platform)!" :alt="row.platform" />
        </span>
        <select :value="row.platform" aria-label="Platform" @change="patch(row, { platform: ($event.target as HTMLSelectElement).value })">
          <option v-for="p in optionsFor(row)" :key="p.value" :value="p.value">{{ p.label }}</option>
        </select>
        <input class="url" type="url" :value="row.url" aria-label="Link" @change="saveUrl(row, $event)" />
        <label class="active">
          <input type="checkbox" :checked="row.active" @change="patch(row, { active: ($event.target as HTMLInputElement).checked })" />
          Active
        </label>
        <button type="button" aria-label="Move up" :disabled="i === 0" @click="move(i, i - 1)">↑</button>
        <button type="button" aria-label="Move down" :disabled="i === rows.length - 1" @click="move(i, i + 1)">↓</button>
        <button type="button" class="danger" @click="social.act(() => adminApi.socialDelete(row.id))">Delete</button>
      </li>
      <li v-if="!social.loading.value && rows.length === 0" class="empty">No links.</li>
    </ul>

    <div class="toolbar" style="margin-top: 0.5rem">
      <select v-model="newPlatform" data-test="social-platform" aria-label="Platform">
        <option v-for="p in SOCIAL_PLATFORMS" :key="p.value" :value="p.value">{{ p.label }}</option>
      </select>
      <input v-model="newUrl" type="url" placeholder="https://…" style="min-width: 18rem" />
      <button class="accent" :disabled="social.loading.value" @click="addSocial">Add</button>
    </div>
  </section>
</template>

<style scoped>
.social-rows { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.4rem; }
.social-row { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; padding: 0.35rem 0.5rem; border: 1px solid #3a3a3a; border-radius: 6px; }
.social-row.over { border-color: var(--accent, #e0a030); }
.social-row.off { opacity: 0.6; }
.handle { cursor: grab; user-select: none; padding: 0 0.25rem; }
.icon-chip { display: inline-flex; align-items: center; justify-content: center; width: 2rem; height: 2rem; border-radius: 50%; background: #1b1b1b; border: 1px solid #3a3a3a; flex: none; }
.icon-chip img { width: 1.1rem; height: 1.1rem; }
.url { flex: 1 1 14rem; min-width: 10rem; }
.active { display: inline-flex; align-items: center; gap: 0.3rem; }
.hint { opacity: 0.7; margin: 0 0 0.5rem; }
.empty { opacity: 0.7; }
</style>
